import { inject, Injectable, Injector, signal } from '@angular/core';
import { handleRequestError } from '@softline/application';
import { DocumentPreviewRepository } from './document-preview.repository';

@Injectable()
export class DocumentPreviewService {
  private readonly repository = inject(DocumentPreviewRepository);
  private readonly injector = inject(Injector);

  readonly base64 = signal<string | null>(null);
  readonly isLoading = signal(false);
  readonly error = signal<unknown>(null);

  load(id: string): Promise<string | null> {
    return this.run(() => this.repository.load(id));
  }

  loadMany(documentIds: string[]): Promise<string | null> {
    if (documentIds.length === 0) {
      this.base64.set(null);
      return Promise.resolve(null);
    }
    return this.run(() => this.repository.loadMany(documentIds));
  }

  loadDocument(id: string): Promise<string | null> {
    return id.startsWith('DRAFT') ? this.load(id) : this.loadMany([id]);
  }

  reset(): void {
    this.base64.set(null);
    this.error.set(null);
  }

  private async run(request: () => Promise<string>): Promise<string | null> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      const result = await request();
      this.base64.set(result);
      return result;
    } catch (e) {
      this.error.set(e);
      handleRequestError(e, this.injector, { show: true });
      return null;
    } finally {
      this.isLoading.set(false);
    }
  }
}
